/**
 * Advanced Essay Prompt Service
 * Multi-source strategy prioritizing IvyCoach for cleaner, more reliable essay prompt data
 * Targets 2025-2026 academic year with fallback strategies
 */

class AdvancedEssayPromptService {

  // Source priority order (IvyCoach first per user request)
  static SOURCES = {
    IVYCOACH: {
      name: 'IvyCoach',
      priority: 1,
      confidence: 95,
      url: 'https://www.ivycoach.com',
      reliability: 'high'
    },
    OFFICIAL: {
      name: 'Official Admissions',
      priority: 2,
      confidence: 90,
      reliability: 'high'
    },
    COLLEGE_CONFIDENTIAL: {
      name: 'College Confidential',
      priority: 3,
      confidence: 75,
      reliability: 'medium'
    },
    FALLBACK: {
      name: 'Multiple Sources',
      priority: 4,
      confidence: 60,
      reliability: 'low'
    }
  };

  // Current academic year for essay prompts
  static CURRENT_YEAR = '2025-2026';
  static FALLBACK_YEAR = '2024-2025';

  /**
   * Enhanced essay prompt fetching with multi-source strategy
   */
  static async fetchEssayPrompts(collegeName, options = {}) {
    console.log(`[AdvancedEssayPrompts] Starting multi-source fetch for ${collegeName}`);
    
    const results = {
      success: false,
      essays: [],
      source: null,
      confidence: 0,
      year: null,
      attempts: [],
      metadata: {}
    };

    // Try each source in priority order
    const sources = this.getSourcesToTry(options);
    
    for (const source of sources) {
      try {
        console.log(`[AdvancedEssayPrompts] Trying source: ${source.name}`);
        
        const attempt = await this.attemptSourceFetch(collegeName, source, options);
        results.attempts.push(attempt);
        
        if (attempt.success && attempt.essays.length > 0) {
          results.success = true;
          results.essays = attempt.essays;
          results.source = source.name;
          results.confidence = source.confidence;
          results.year = attempt.year;
          results.metadata = attempt.metadata;
          
          console.log(`[AdvancedEssayPrompts] ✅ Success with ${source.name}: ${attempt.essays.length} essays found`);
          break;
        }
        
      } catch (error) {
        console.warn(`[AdvancedEssayPrompts] Source ${source.name} failed:`, error.message);
        results.attempts.push({
          source: source.name,
          success: false,
          error: error.message,
          timestamp: new Date().toISOString()
        });
      }
    }

    // If no source succeeded, return consolidated error info
    if (!results.success) {
      results.error = 'All sources failed';
      results.source = 'none';
      console.log(`[AdvancedEssayPrompts] ❌ All sources failed for ${collegeName}`);
    }

    return results;
  }

  /**
   * Get sources to try based on options
   */
  static getSourcesToTry(options = {}) {
    const allSources = Object.values(this.SOURCES);
    
    // If force source is specified, try that first
    if (options.forceSource) {
      const forcedSource = allSources.find(s => s.name === options.forceSource);
      if (forcedSource) {
        return [forcedSource, ...allSources.filter(s => s.name !== options.forceSource)];
      }
    }
    
    // Default: try in priority order
    return allSources.sort((a, b) => a.priority - b.priority);
  }

  /**
   * Attempt to fetch from a specific source
   */
  static async attemptSourceFetch(collegeName, source, options = {}) {
    const attempt = {
      source: source.name,
      success: false,
      essays: [],
      year: null,
      metadata: {},
      timestamp: new Date().toISOString()
    };

    try {
      switch (source.name) {
        case 'IvyCoach':
          return await this.fetchFromIvyCoach(collegeName, attempt, options);
        
        case 'Official Admissions':
          return await this.fetchFromOfficialSite(collegeName, attempt, options);
        
        case 'College Confidential':
          return await this.fetchFromCollegeConfidential(collegeName, attempt, options);
        
        case 'Multiple Sources':
          return await this.fetchFromFallbackSources(collegeName, attempt, options);
        
        default:
          throw new Error(`Unknown source: ${source.name}`);
      }
      
    } catch (error) {
      attempt.error = error.message;
      throw error;
    }
  }

  /**
   * Fetch from IvyCoach (Primary source - cleaner data structure)
   */
  static async fetchFromIvyCoach(collegeName, attempt, options = {}) {
    console.log(`[AdvancedEssayPrompts] Fetching from IvyCoach for ${collegeName}`);
    
    // Construct IvyCoach-optimized API request
    const requestPayload = {
      action: 'fetch-essay-prompts',
      collegeName: collegeName,
      year: this.CURRENT_YEAR,
      searchVariations: [collegeName]
    };

    const response = await fetch('/api/anthropic/suggestions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestPayload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`IvyCoach API 400 error details:`, errorText);
      throw new Error(`IvyCoach API request failed: ${response.status} - ${errorText.substring(0, 200)}`);
    }

    const result = await response.json();

    if (result.success && result.data) {
      const essays = result.data.essays || [];
      
      attempt.success = essays.length > 0;
      attempt.essays = essays;
      attempt.year = result.data.prompt_year || result.year || this.CURRENT_YEAR;
      attempt.metadata = {
        rawResponse: result.data,
        confidence: result.data.confidence_score || 70,
        parsing_method: 'api_structured',
        source: result.data.source || 'IvyCoach',
        verification_status: result.data.verification_status || 'ai_fetched'
      };
      
      return attempt;
    }

    throw new Error('IvyCoach returned no usable data');
  }

  /**
   * Parse IvyCoach response into structured essay format
   */
  static parseIvyCoachResponse(data) {
    const essays = [];
    
    // IvyCoach typically has cleaner, more structured data
    if (data.essay_requirements) {
      const req = data.essay_requirements;
      
      // Process supplemental essays
      if (req.supplemental_essays && Array.isArray(req.supplemental_essays)) {
        req.supplemental_essays.forEach((essay, index) => {
          essays.push({
            prompt: essay.prompt || essay.question,
            word_limit: essay.word_limit || this.extractWordLimit(essay.prompt),
            type: this.detectEssayType(essay.prompt),
            required: essay.required !== false,
            source_confidence: 95,
            essay_id: `supp_${index + 1}`,
            metadata: {
              original_data: essay,
              source_section: 'supplemental_essays'
            }
          });
        });
      }
      
      // Process Why Us essay if specified
      if (req.why_us_essay) {
        essays.push({
          prompt: req.why_us_prompt || `Why do you want to attend ${data.college_name || 'this college'}?`,
          word_limit: req.why_us_word_limit || 300,
          type: 'why_us',
          required: true,
          source_confidence: 90,
          essay_id: 'why_us',
          metadata: {
            source_section: 'why_us_essay'
          }
        });
      }
    }

    // Process essays from other response formats
    if (data.essays && Array.isArray(data.essays)) {
      data.essays.forEach((essay, index) => {
        if (essay.prompt) {
          essays.push({
            prompt: essay.prompt,
            word_limit: essay.word_limit || this.extractWordLimit(essay.prompt),
            type: essay.type || this.detectEssayType(essay.prompt),
            required: essay.required !== false,
            source_confidence: 90,
            essay_id: `parsed_${index + 1}`,
            metadata: {
              original_data: essay
            }
          });
        }
      });
    }

    return this.deduplicateEssays(essays);
  }

  /**
   * Fetch from Official Admissions Site (Secondary source)
   */
  static async fetchFromOfficialSite(collegeName, attempt, options = {}) {
    console.log(`[AdvancedEssayPrompts] Fetching from Official Site for ${collegeName}`);
    
    const requestPayload = {
      action: 'fetch-essay-prompts',
      collegeName: collegeName,
      year: this.CURRENT_YEAR,
      searchVariations: [collegeName]
    };

    const response = await fetch('/api/anthropic/suggestions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestPayload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Official Site API 400 error details:`, errorText);
      throw new Error(`Official site API request failed: ${response.status} - ${errorText.substring(0, 200)}`);
    }

    const result = await response.json();

    if (result.success && result.data) {
      const essays = result.data.essays || [];
      
      attempt.success = essays.length > 0;
      attempt.essays = essays;
      attempt.year = result.data.prompt_year || result.year || this.CURRENT_YEAR;
      attempt.metadata = {
        rawResponse: result.data,
        confidence: result.data.confidence_score || 70,
        parsing_method: 'api_structured',
        source: result.data.source || 'Official Site',
        verification_status: result.data.verification_status || 'ai_fetched'
      };
      
      return attempt;
    }

    throw new Error('Official site returned no usable data');
  }

  /**
   * Fetch from College Confidential
   */
  static async fetchFromCollegeConfidential(collegeName, attempt, options = {}) {
    console.log(`[AdvancedEssayPrompts] Fetching from College Confidential for ${collegeName}`);
    
    const requestPayload = {
      action: 'fetch-essay-prompts',
      collegeName: collegeName,
      year: this.CURRENT_YEAR,
      searchVariations: [collegeName]
    };

    const response = await fetch('/api/anthropic/suggestions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestPayload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`College Confidential API 400 error details:`, errorText);
      throw new Error(`College Confidential API request failed: ${response.status} - ${errorText.substring(0, 200)}`);
    }

    const result = await response.json();

    if (result.success && result.data) {
      const essays = result.data.essays || [];
      
      attempt.success = essays.length > 0;
      attempt.essays = essays;
      attempt.year = result.data.prompt_year || result.year || this.CURRENT_YEAR;
      attempt.metadata = {
        rawResponse: result.data,
        confidence: result.data.confidence_score || 70,
        parsing_method: 'api_structured',
        source: result.data.source || 'College Confidential',
        verification_status: result.data.verification_status || 'ai_fetched',
        reliability_note: 'Community-sourced data may require verification'
      };
      
      return attempt;
    }

    throw new Error('College Confidential returned no usable data');
  }

  /**
   * Parse College Confidential response
   */
  static parseCollegeConfidentialResponse(data) {
    // Similar parsing logic but with lower confidence scores
    const essays = [];
    
    if (data.essay_requirements) {
      const req = data.essay_requirements;
      
      if (req.supplemental_essays && Array.isArray(req.supplemental_essays)) {
        req.supplemental_essays.forEach((essay, index) => {
          essays.push({
            prompt: essay.prompt || essay.question,
            word_limit: essay.word_limit || this.extractWordLimit(essay.prompt),
            type: this.detectEssayType(essay.prompt),
            required: essay.required !== false,
            source_confidence: 75, // Lower confidence for community data
            essay_id: `cc_supp_${index + 1}`,
            metadata: {
              source: 'college_confidential',
              community_verified: essay.community_verified || false
            }
          });
        });
      }
    }
    
    return essays;
  }

  /**
   * Fetch from fallback sources (last resort)
   */
  static async fetchFromFallbackSources(collegeName, attempt, options = {}) {
    console.log(`[AdvancedEssayPrompts] Fetching from fallback sources for ${collegeName}`);
    
    // Try previous year as fallback
    const requestPayload = {
      action: 'fetch-essay-prompts',
      collegeName: collegeName,
      year: this.FALLBACK_YEAR,
      searchVariations: [collegeName]
    };

    const response = await fetch('/api/anthropic/suggestions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestPayload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Fallback API 400 error details:`, errorText);
      throw new Error(`Fallback API request failed: ${response.status} - ${errorText.substring(0, 200)}`);
    }

    const result = await response.json();

    if (result.success && result.data) {
      const essays = result.data.essays || [];
      
      attempt.success = essays.length > 0;
      attempt.essays = essays;
      attempt.year = result.data.prompt_year || result.year || this.FALLBACK_YEAR;
      attempt.metadata = {
        rawResponse: result.data,
        confidence: result.data.confidence_score || 50,
        parsing_method: 'api_structured',
        source: result.data.source || 'Fallback Sources',
        verification_status: result.data.verification_status || 'ai_fetched',
        year_warning: `Data may be from ${this.FALLBACK_YEAR} academic year`
      };
      
      return attempt;
    }

    throw new Error('Fallback sources returned no usable data');
  }

  /**
   * Parse official site response
   */
  static parseOfficialResponse(data) {
    const essays = [];
    
    // Use similar parsing logic but with official site expectations
    if (data.essay_requirements?.essays) {
      data.essay_requirements.essays.forEach((essay, index) => {
        essays.push({
          prompt: essay.prompt,
          word_limit: essay.word_limit || this.extractWordLimit(essay.prompt),
          type: this.detectEssayType(essay.prompt),
          required: essay.required !== false,
          source_confidence: 85,
          essay_id: `official_${index + 1}`,
          metadata: {
            original_data: essay,
            source: 'official_admissions'
          }
        });
      });
    }

    return this.deduplicateEssays(essays);
  }

  /**
   * Parse fallback response (may include older data)
   */
  static parseFallbackResponse(data) {
    const essays = [];
    
    // More liberal parsing for fallback data
    const possibleEssays = [
      ...(data.essay_requirements?.essays || []),
      ...(data.essays || []),
      ...(data.supplemental_essays || [])
    ];

    possibleEssays.forEach((essay, index) => {
      if (essay.prompt || essay.question) {
        essays.push({
          prompt: essay.prompt || essay.question,
          word_limit: essay.word_limit || this.extractWordLimit(essay.prompt || essay.question),
          type: this.detectEssayType(essay.prompt || essay.question),
          required: essay.required !== false,
          source_confidence: 60,
          essay_id: `fallback_${index + 1}`,
          metadata: {
            original_data: essay,
            source: 'fallback_sources',
            year_uncertainty: true
          }
        });
      }
    });

    return this.deduplicateEssays(essays);
  }

  /**
   * Extract word limit from prompt text
   */
  static extractWordLimit(text) {
    if (!text) return null;
    
    const patterns = [
      /(\d+)\s*words?/i,
      /(\d+)-word/i,
      /maximum\s+of\s+(\d+)/i,
      /up\s+to\s+(\d+)/i,
      /limit\s*:\s*(\d+)/i,
      /(\d+)\s*character/i // Convert characters to approximate words
    ];
    
    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match) {
        let limit = parseInt(match[1]);
        
        // Convert character limits to word estimates (avg 5 chars per word)
        if (pattern.source.includes('character')) {
          limit = Math.round(limit / 5);
        }
        
        return limit;
      }
    }
    
    // Default word limits by essay type if not specified
    return 250;
  }

  /**
   * Detect essay type from prompt content
   */
  static detectEssayType(prompt) {
    if (!prompt) return 'general';
    
    const lowerPrompt = prompt.toLowerCase();
    
    if (lowerPrompt.includes('why us') || lowerPrompt.includes('why our') || 
        lowerPrompt.includes('why do you want') || lowerPrompt.includes('interest in')) {
      return 'why_us';
    } else if (lowerPrompt.includes('major') || lowerPrompt.includes('field of study')) {
      return 'academic_interest';
    } else if (lowerPrompt.includes('diversity') || lowerPrompt.includes('background')) {
      return 'diversity';
    } else if (lowerPrompt.includes('leadership') || lowerPrompt.includes('lead')) {
      return 'leadership';
    } else if (lowerPrompt.includes('community') || lowerPrompt.includes('contribute')) {
      return 'community';
    } else if (lowerPrompt.includes('challenge') || lowerPrompt.includes('overcome')) {
      return 'challenge';
    }
    
    return 'supplemental';
  }

  /**
   * Remove duplicate essays based on similar prompts
   */
  static deduplicateEssays(essays) {
    const unique = [];
    const seen = new Set();
    
    essays.forEach(essay => {
      // Create a normalized version for comparison
      const normalized = essay.prompt.toLowerCase()
        .replace(/[^\w\s]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      
      if (!seen.has(normalized)) {
        seen.add(normalized);
        unique.push(essay);
      }
    });
    
    return unique;
  }

  /**
   * Calculate confidence score based on source and data quality
   */
  static calculateConfidence(data, sourceType) {
    let baseConfidence = this.SOURCES[sourceType.toUpperCase()]?.confidence || 50;
    
    // Adjust based on data quality indicators
    if (data.verification_status === 'verified') baseConfidence += 10;
    if (data.year && data.year.includes(this.CURRENT_YEAR)) baseConfidence += 5;
    if (data.source_url) baseConfidence += 5;
    
    return Math.min(baseConfidence, 100);
  }

  /**
   * Assess word limit accuracy
   */
  static assessWordLimitAccuracy(essays) {
    const withLimits = essays.filter(e => e.word_limit && e.word_limit > 0).length;
    const total = essays.length;
    
    if (total === 0) return 0;
    
    return Math.round((withLimits / total) * 100);
  }

  /**
   * Get formatted result for UI display
   */
  static formatResultForUI(result) {
    if (!result.success) {
      return {
        success: false,
        message: 'No essay prompts found',
        essays: [],
        source: 'none',
        confidence: 0
      };
    }

    const confidenceText = result.confidence >= 90 ? 'High confidence' : 
                          result.confidence >= 70 ? 'Good confidence' : 'Needs verification';

    const yearWarning = result.year === this.FALLBACK_YEAR ? 
      ` (${this.FALLBACK_YEAR} data - verify for current year)` : '';

    return {
      success: true,
      message: `Found ${result.essays.length} essays from ${result.source} (${confidenceText})${yearWarning}`,
      essays: result.essays,
      source: result.source,
      confidence: result.confidence,
      year: result.year,
      metadata: result.metadata
    };
  }
}

export default AdvancedEssayPromptService;